/**
 * Advanced Calculator Types
 *
 * Types for Professional Tariff Calculator (Kalkulator Zaawansowany)
 * Allows users to input exact invoice amounts and compare simulations for other tariffs.
 *
 * @see /requirements/professionalTariffCalculator.txt - Complete requirements
 */

import type { TariffType } from './calculator';

// Re-export TariffType for convenience
export type { TariffType };

// ============================================================================
// INVOICE PARAMETERS (11 main parameters)
// ============================================================================

/**
 * Invoice Parameter Names
 * Maps to exact invoice line items from energy bill
 */
export type InvoiceParameterType =
  // ENERGY SALES (2 parameters)
  | 'energyElectricCost'        // Energia elektryczna czynna (zł/kWh)
  | 'handlingFee'                // Opłata handlowa (zł/miesiąc)

  // DISTRIBUTION - NETWORK SERVICES (9 parameters)
  | 'networkFixedFee'            // Składnik stały stawki sieciowej (zł/miesiąc)
  | 'transitionFee'              // Stawka opłaty przejściowej (zł/miesiąc)
  | 'qualityFee'                 // Stawka jakościowa (zł/kWh)
  | 'networkVariableFee'         // Składnik zmienny stawki sieciowej (zł/kWh)
  | 'ozeFee'                     // Opłata OZE (zł/kWh)
  | 'cogenerationFee'            // Opłata kogeneracyjna (zł/kWh)
  | 'subscriptionFee'            // Stawka opłaty abonamentowej (zł/miesiąc)
  | 'powerFee';                  // Opłata mocowa (zł/miesiąc)

/**
 * Invoice Parameter Metadata
 * UI labels, descriptions, and units for each parameter
 */
export interface InvoiceParameterMetadata {
  name: string;                  // Display name (Polish)
  description: string;           // Short description
  unit: 'PLN_PER_KWH' | 'PLN_PER_MONTH'; // Unit type
  category: 'ENERGY_SALES' | 'DISTRIBUTION'; // Category
  isVariable: boolean;           // Variable (per kWh) or fixed (per month)
}

/**
 * Invoice Parameter Value (4-value system)
 *
 * Priority: (c) → (b) → (a)
 * - (a) valueFromBasic: From Basic Calculator or operator tariff (default)
 * - (b) valueFromInvoice: Imported from PDF invoice (MVP: always empty)
 * - (c) valueOverridden: User manually entered
 * - (d) valueFinal: Final value used in calculations (c → b → a)
 */
export interface InvoiceParameterValue {
  valueFromBasic: number | null;      // (a) From Basic Calculator / Tariff
  valueFromInvoice: number | null;    // (b) From PDF import (MVP: always null)
  valueOverridden: number | null;     // (c) User override
  valueFinal: number;                 // (d) Final value (priority: c → b → a)
}

/**
 * Monthly Invoice Parameters
 * All 11 parameters for a single month
 */
export interface MonthlyInvoiceParameters {
  // Energy Sales (2 parameters)
  energyElectricCost: InvoiceParameterValue;
  handlingFee: InvoiceParameterValue;

  // Distribution (9 parameters)
  networkFixedFee: InvoiceParameterValue;
  transitionFee: InvoiceParameterValue;
  qualityFee: InvoiceParameterValue;
  networkVariableFee: InvoiceParameterValue;
  ozeFee: InvoiceParameterValue;
  cogenerationFee: InvoiceParameterValue;
  subscriptionFee: InvoiceParameterValue;
  powerFee: InvoiceParameterValue;
}

// ============================================================================
// HOURLY PATTERN OPTIONS (for Advanced Calculator)
// ============================================================================

/**
 * Hourly Pattern Type (Advanced Calculator)
 * Options for monthly hourly consumption pattern
 */
export type ProHourlyPatternType =
  | 'UNIFORM'              // Równomierne - identical usage every hour
  | 'MORNINGS_EVENINGS'    // Poranki i wieczory - high usage mornings (6-9) and evenings (17-22)
  | 'WORK_FROM_HOME'       // Praca w domu - high usage during day (9-17)
  | 'NIGHT_ELECTRIC'       // Noc elektryczna - high usage at night (22-6)
  | 'FROM_CSV_IMPORT';     // Z importu CSV - use pattern from imported CSV hourly data

/**
 * Hourly Pattern Metadata
 * UI labels and descriptions for each pattern option
 */
export interface ProHourlyPatternMetadata {
  type: ProHourlyPatternType;
  name: string;                  // Display name (Polish)
  description: string;           // Short description
  icon?: string;                 // Icon identifier (optional)
  weekdayPattern?: number[];     // Hourly distribution for weekdays (24 values, sum = 1.0)
  weekendPattern?: number[];     // Hourly distribution for weekends (24 values, sum = 1.0)
}

// ============================================================================
// MONTHLY CONFIGURATION
// ============================================================================

/**
 * Month Index (0-11)
 * January = 0, December = 11
 */
export type MonthIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;

/**
 * Monthly Configuration
 * Tariff, consumption, and pattern for a single month
 */
export interface MonthlyConfiguration {
  tariff: TariffType;                    // Selected tariff for this month
  consumption: number;                   // Monthly consumption (kWh)
  hourlyPattern: ProHourlyPatternType;   // Hourly pattern type
  parameters: MonthlyInvoiceParameters;  // Invoice parameters (11 values)
}

/**
 * Tariff Category Type
 * Distinguishes household (G) from business (C) tariffs
 */
export type TariffCategoryType = 'HOUSEHOLD' | 'BUSINESS';

/**
 * Business Power Group Type
 * C1: ≤40 kW, C2: >40 kW
 */
export type BusinessPowerGroupType = 'C1' | 'C2';

/**
 * Advanced Calculator Form Values
 * Complete form state for Advanced Calculator
 */
export interface ProCalculatorFormValues {
  calculationYear: number;                          // Year (e.g., 2025)
  distributor: string;                              // Operator (MVP: "TAURON" only)
  handlingFeeType: HandlingFeeType;                 // Handling fee type (REGULATED or COMMERCIAL)

  // === BUSINESS TARIFF SUPPORT (NEW) ===
  tariffCategory: TariffCategoryType;               // HOUSEHOLD (G) or BUSINESS (C)
  contractedPower?: number;                         // kW - only for BUSINESS (moc umowna)
  businessPowerGroup?: BusinessPowerGroupType;     // C1 (≤40kW) or C2 (>40kW)

  // Monthly configurations (12 months)
  months: [
    MonthlyConfiguration, // January
    MonthlyConfiguration, // February
    MonthlyConfiguration, // March
    MonthlyConfiguration, // April
    MonthlyConfiguration, // May
    MonthlyConfiguration, // June
    MonthlyConfiguration, // July
    MonthlyConfiguration, // August
    MonthlyConfiguration, // September
    MonthlyConfiguration, // October
    MonthlyConfiguration, // November
    MonthlyConfiguration  // December
  ];
}

// ============================================================================
// SIMULATION RESULTS
// ============================================================================

/**
 * Monthly Cost Breakdown
 * Detailed cost breakdown for a single month
 */
export interface MonthlyCostBreakdown {
  monthIndex: number;                    // 0-11 (Jan-Dec)
  totalCost: number;                     // PLN (gross) - total monthly cost
  energyCost: number;                    // PLN (gross) - energy cost (variable + fixed)
  distributionCost: number;              // PLN (gross) - distribution cost (variable + fixed)
  fixedCost: number;                     // PLN (gross) - all fixed fees together

  // Optional: detailed breakdown (if backend provides it)
  energyVariableCost?: number;           // PLN (gross) - energy variable cost only
  energyFixedCost?: number;              // PLN (gross) - energy fixed cost only (handling fee)
  distributionVariableCost?: number;     // PLN (gross) - distribution variable cost only
  distributionFixedCost?: number;        // PLN (gross) - distribution fixed cost only
}

/**
 * Tariff Simulation Result
 * "What-if" simulation for a single tariff
 */
export interface TariffSimulationResult {
  tariffCode: TariffType;
  tariffName: string;
  annualCost: number;                    // PLN (gross)
  monthlyCost: number;                   // PLN (gross) - average
  savings: number;                       // PLN (gross) - vs baseline
  savingsPercent: number;                // % savings vs baseline
  isBaseline: boolean;                   // True if this is the baseline tariff
  isRecommended: boolean;                // True if this is the cheapest option
  monthlyBreakdown?: MonthlyCostBreakdown[]; // Optional monthly cost breakdown (12 items)
}

/**
 * G14 Simulation Result
 * Additional data for G14 dynamic tariff (RCE price statistics)
 */
export interface G14SimulationResult extends TariffSimulationResult {
  rcePriceStats?: {
    avgPricePerKwh: number;              // PLN/kWh (gross) - average RCE price
    minPricePerKwh: number;              // PLN/kWh (gross) - minimum RCE price
    maxPricePerKwh: number;              // PLN/kWh (gross) - maximum RCE price
  };
}

/**
 * Optimization Recommendation
 * For future use: AI-driven recommendations for tariff optimization
 */
export interface OptimizationRecommendation {
  tariffCode: TariffType;
  reason: string;
  savingsPotential: number;              // PLN (gross)
  actionItems: string[];
}

/**
 * Household simulations type (G-series)
 */
export interface HouseholdSimulations {
  G11: TariffSimulationResult;
  G12: TariffSimulationResult;
  G12w: TariffSimulationResult;
  G12as: TariffSimulationResult;
  G12n?: TariffSimulationResult;       // Optional - PGE-specific
  G12r?: TariffSimulationResult;       // Optional - Energa-specific
  G13: TariffSimulationResult;
  G13s: TariffSimulationResult;
  G14?: G14SimulationResult;           // Optional - only if G14 price data available
}

/**
 * Business simulations type (C-series)
 */
export interface BusinessSimulations {
  C11?: TariffSimulationResult;
  C12a?: TariffSimulationResult;
  C12b?: TariffSimulationResult;
  C21?: TariffSimulationResult;
  C22a?: TariffSimulationResult;
  C22b?: TariffSimulationResult;
}

/**
 * Advanced Calculator Results
 * Complete simulation results for all tariffs
 */
export interface ProCalculatorResults {
  input: ProCalculatorFormValues;

  // Simulations for household tariffs (G-series)
  simulations: HouseholdSimulations;

  // Simulations for business tariffs (C-series) - optional
  businessSimulations?: BusinessSimulations;

  recommendedTariff: TariffType;         // Cheapest tariff
  potentialSavings: number;              // PLN (gross) - max savings
  savingsPercent: number;                // % savings - max

  calculatedAt: Date;
  optimizationRecommendations?: OptimizationRecommendation[]; // Future: AI recommendations
}

// ============================================================================
// UI STATE
// ============================================================================

/**
 * Advanced Calculator UI State
 * State management for Advanced Calculator component
 */
export interface ProCalculatorUIState {
  formValues: ProCalculatorFormValues | null;
  results: ProCalculatorResults | null;
  baselineTariff: TariffType;            // Baseline for comparison (clickable)
  isLoading: boolean;
  isCalculating: boolean;
  error: string | null;
}

/**
 * Editable Cell State
 * State for inline editing of parameter values
 */
export interface EditableCellState {
  month: MonthIndex;
  parameter: InvoiceParameterType;
  isEditing: boolean;
  tempValue: string;                     // Temporary value during edit
}

// ============================================================================
// HANDLING FEE TYPES
// ============================================================================

/**
 * Handling Fee Type
 * Controls which handling fee rate is used in calculations
 */
export type HandlingFeeType = 'REGULATED' | 'COMMERCIAL';

/**
 * Handling Fee Constants (PLN/month, net - before VAT)
 * Matches backend HANDLING_FEE constants
 *
 * IMPORTANT: "Taryfa sprzedawcy z urzędu" (URE regulated) has NO handling fee (0 zł)
 * Commercial tariffs charge 10-200+ zł/month depending on provider
 */
export const HANDLING_FEE = {
  REGULATED: 0,              // Taryfa sprzedawcy z urzędu - brak opłaty handlowej
  COMMERCIAL_BASIC: 15.00,   // Standard commercial tariff (Energa ~20 zł, Tauron ~15-20 zł)
  COMMERCIAL_PREMIUM: 20.00, // Commercial with premium services
};

/**
 * Handling Fee Metadata
 * UI labels and descriptions for handling fee options
 */
export const HANDLING_FEE_METADATA: Record<HandlingFeeType, {
  name: string;
  description: string;
  priceNet: number;
  priceGross: number;
}> = {
  REGULATED: {
    name: 'Taryfa sprzedawcy z urzędu',
    description: 'Brak opłaty handlowej (0 zł). Oszczędzasz ~180-240 zł rocznie vs taryfa komercyjna.',
    priceNet: HANDLING_FEE.REGULATED,
    priceGross: HANDLING_FEE.REGULATED * 1.23,
  },
  COMMERCIAL: {
    name: 'Taryfa komercyjna',
    description: 'Opłata handlowa 15-20 zł/mies. u sprzedawców komercyjnych (Tauron, Energa, Enea, PGE).',
    priceNet: HANDLING_FEE.COMMERCIAL_PREMIUM,
    priceGross: HANDLING_FEE.COMMERCIAL_PREMIUM * 1.23,
  },
};

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Invoice Parameter Metadata Map
 * UI metadata for all 11 parameters
 */
export const INVOICE_PARAMETER_METADATA: Record<InvoiceParameterType, InvoiceParameterMetadata> = {
  energyElectricCost: {
    name: 'Energia elektryczna czynna',
    description: 'Ilość energii sprzedanej w danej strefie czasowej',
    unit: 'PLN_PER_KWH',
    category: 'ENERGY_SALES',
    isVariable: true,
  },
  handlingFee: {
    name: 'Opłata handlowa',
    description: 'Stała opłata miesięczna sprzedawcy. Taryfa URE: 0 zł. Taryfa komercyjna: 15-20 zł/mies.',
    unit: 'PLN_PER_MONTH',
    category: 'ENERGY_SALES',
    isVariable: false,
  },
  networkFixedFee: {
    name: 'Składnik stały stawki sieciowej',
    description: 'Abonament dystrybucyjny',
    unit: 'PLN_PER_MONTH',
    category: 'DISTRIBUTION',
    isVariable: false,
  },
  transitionFee: {
    name: 'Stawka opłaty przejściowej',
    description: 'Opłata za transformację napięcia',
    unit: 'PLN_PER_MONTH',
    category: 'DISTRIBUTION',
    isVariable: false,
  },
  qualityFee: {
    name: 'Stawka jakościowa',
    description: 'Opłata za jakość prądu',
    unit: 'PLN_PER_KWH',
    category: 'DISTRIBUTION',
    isVariable: true,
  },
  networkVariableFee: {
    name: 'Składnik zmienny stawki sieciowej',
    description: 'Dystrybucja za energię',
    unit: 'PLN_PER_KWH',
    category: 'DISTRIBUTION',
    isVariable: true,
  },
  ozeFee: {
    name: 'Opłata OZE',
    description: 'Opłata na odnawialne źródła energii',
    unit: 'PLN_PER_KWH',
    category: 'DISTRIBUTION',
    isVariable: true,
  },
  cogenerationFee: {
    name: 'Opłata kogeneracyjna',
    description: 'Wsparcie kogeneracji',
    unit: 'PLN_PER_KWH',
    category: 'DISTRIBUTION',
    isVariable: true,
  },
  subscriptionFee: {
    name: 'Stawka opłaty abonamentowej',
    description: 'Abonament za licznik',
    unit: 'PLN_PER_MONTH',
    category: 'DISTRIBUTION',
    isVariable: false,
  },
  powerFee: {
    name: 'Opłata mocowa',
    description: 'Opłata za moc przyłączeniową',
    unit: 'PLN_PER_MONTH',
    category: 'DISTRIBUTION',
    isVariable: false,
  },
};

/**
 * Hourly Pattern Metadata Map
 * UI metadata for all hourly pattern options
 */
export const PRO_HOURLY_PATTERN_METADATA: Record<ProHourlyPatternType, ProHourlyPatternMetadata> = {
  UNIFORM: {
    type: 'UNIFORM',
    name: 'Równomierne',
    description: 'Równomierny rozkład 24h',
  },
  MORNINGS_EVENINGS: {
    type: 'MORNINGS_EVENINGS',
    name: 'Poranki i wieczory',
    description: 'Wyższe zużycie 6-9h i 17-22h',
    // Weekday: high usage 6-9h (morning) and 17-22h (evening)
    weekdayPattern: [
      0.01, 0.01, 0.01, 0.01, 0.01, 0.02,  // 0-5h: very low
      0.08, 0.09, 0.08, 0.06,              // 6-9h: morning peak
      0.03, 0.03, 0.03, 0.03,              // 10-13h: moderate
      0.03, 0.03, 0.04, 0.07,              // 14-17h: increasing
      0.08, 0.09, 0.08, 0.06,              // 18-21h: evening peak
      0.04, 0.02,                          // 22-23h: declining
    ],
    // Weekend: more balanced but still peaks in mornings/evenings
    weekendPattern: [
      0.01, 0.01, 0.01, 0.01, 0.01, 0.01,  // 0-5h: very low
      0.02, 0.04, 0.06, 0.07,              // 6-9h: gradual morning rise
      0.06, 0.06, 0.06, 0.06,              // 10-13h: steady
      0.05, 0.05, 0.05, 0.06,              // 14-17h: steady
      0.07, 0.08, 0.07, 0.06,              // 18-21h: evening peak
      0.04, 0.02,                          // 22-23h: declining
    ],
  },
  WORK_FROM_HOME: {
    type: 'WORK_FROM_HOME',
    name: 'Praca w domu',
    description: 'Wyższe zużycie w godzinach pracy (9-17h)',
    // Weekday: high usage during work hours 9-17h
    weekdayPattern: [
      0.01, 0.01, 0.01, 0.01, 0.01, 0.01,  // 0-5h: very low
      0.02, 0.03, 0.04, 0.07,              // 6-9h: morning startup
      0.08, 0.09, 0.09, 0.08,              // 10-13h: work peak
      0.08, 0.08, 0.07, 0.06,              // 14-17h: work continues
      0.05, 0.04, 0.04, 0.03,              // 18-21h: winding down
      0.02, 0.01,                          // 22-23h: low
    ],
    // Weekend: more uniform, no work pattern
    weekendPattern: [
      0.01, 0.01, 0.01, 0.01, 0.01, 0.01,  // 0-5h: very low
      0.02, 0.03, 0.04, 0.05,              // 6-9h: gradual rise
      0.06, 0.06, 0.06, 0.06,              // 10-13h: steady
      0.06, 0.06, 0.06, 0.06,              // 14-17h: steady
      0.06, 0.06, 0.05, 0.04,              // 18-21h: steady decline
      0.03, 0.02,                          // 22-23h: low
    ],
  },
  NIGHT_ELECTRIC: {
    type: 'NIGHT_ELECTRIC',
    name: 'Noc elektryczna',
    description: 'Wyższe zużycie w nocy (22-6h)',
    // Weekday: high usage at night (electric heating, water heater on cheap tariff)
    weekdayPattern: [
      0.08, 0.08, 0.08, 0.08, 0.08, 0.07,  // 0-5h: night peak
      0.04, 0.02, 0.02, 0.02,              // 6-9h: morning low
      0.02, 0.02, 0.02, 0.02,              // 10-13h: daytime low
      0.02, 0.02, 0.02, 0.02,              // 14-17h: daytime low
      0.03, 0.03, 0.04, 0.05,              // 18-21h: evening rise
      0.07, 0.08,                          // 22-23h: night peak starts
    ],
    // Weekend: similar night pattern
    weekendPattern: [
      0.08, 0.08, 0.08, 0.08, 0.08, 0.07,  // 0-5h: night peak
      0.05, 0.03, 0.02, 0.02,              // 6-9h: morning decline
      0.02, 0.02, 0.02, 0.02,              // 10-13h: daytime low
      0.02, 0.02, 0.02, 0.02,              // 14-17h: daytime low
      0.03, 0.03, 0.04, 0.05,              // 18-21h: evening rise
      0.07, 0.08,                          // 22-23h: night peak starts
    ],
  },
  FROM_CSV_IMPORT: {
    type: 'FROM_CSV_IMPORT',
    name: 'Z importu CSV',
    description: 'Użyj wzorca z zaimportowanych danych godzinowych CSV',
  },
};

/**
 * Month Names (Polish)
 * Full month names for display
 */
export const PRO_MONTH_NAMES: Record<MonthIndex, string> = {
  0: 'Styczeń',
  1: 'Luty',
  2: 'Marzec',
  3: 'Kwiecień',
  4: 'Maj',
  5: 'Czerwiec',
  6: 'Lipiec',
  7: 'Sierpień',
  8: 'Wrzesień',
  9: 'Październik',
  10: 'Listopad',
  11: 'Grudzień',
};

/**
 * Month Names Short (Polish)
 * Short month names for compact display
 */
export const PRO_MONTH_NAMES_SHORT: Record<MonthIndex, string> = {
  0: 'Sty',
  1: 'Lut',
  2: 'Mar',
  3: 'Kwi',
  4: 'Maj',
  5: 'Cze',
  6: 'Lip',
  7: 'Sie',
  8: 'Wrz',
  9: 'Paź',
  10: 'Lis',
  11: 'Gru',
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate final value (d) from 4-value system
 * Priority: (c) → (b) → (a)
 */
export function calculateFinalValue(
  valueFromBasic: number | null,
  valueFromInvoice: number | null,
  valueOverridden: number | null
): number {
  if (valueOverridden !== null && valueOverridden !== undefined) {
    return valueOverridden; // Priority 1: User override
  }

  if (valueFromInvoice !== null && valueFromInvoice !== undefined) {
    return valueFromInvoice; // Priority 2: From invoice
  }

  return valueFromBasic || 0; // Priority 3: From basic or 0
}

/**
 * Create empty InvoiceParameterValue
 * Used for initialization
 */
export function createEmptyParameterValue(): InvoiceParameterValue {
  return {
    valueFromBasic: null,
    valueFromInvoice: null,
    valueOverridden: null,
    valueFinal: 0,
  };
}

/**
 * Create empty MonthlyInvoiceParameters
 * Used for initialization
 */
export function createEmptyMonthlyParameters(): MonthlyInvoiceParameters {
  return {
    energyElectricCost: createEmptyParameterValue(),
    handlingFee: createEmptyParameterValue(),
    networkFixedFee: createEmptyParameterValue(),
    transitionFee: createEmptyParameterValue(),
    qualityFee: createEmptyParameterValue(),
    networkVariableFee: createEmptyParameterValue(),
    ozeFee: createEmptyParameterValue(),
    cogenerationFee: createEmptyParameterValue(),
    subscriptionFee: createEmptyParameterValue(),
    powerFee: createEmptyParameterValue(),
  };
}

/**
 * Create default MonthlyConfiguration
 * Used for initialization
 */
export function createDefaultMonthlyConfig(): MonthlyConfiguration {
  return {
    tariff: 'G11',
    consumption: 0,
    hourlyPattern: 'UNIFORM',
    parameters: createEmptyMonthlyParameters(),
  };
}
